import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { addToast, Button, Chip, useDisclosure } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useDeleteTeam, useUpdateTeam } from '~/hooks/useTeams';
import { useTenant } from '~/hooks/useTenant';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { Input, Textarea } from '~/components/Field';
import { LoadingSpinner } from '~/components/LoadingSpinner';

import type { Team } from '~/types';

interface TeamSettingsProps {
  team: Team;
}

const updateTeamSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, 'Team name must be at least 3 characters')
    .max(255, 'Team name must be less than 255 characters'),
  description: z
    .string()
    .max(1000, 'Description must be less than 1000 characters')
    .optional()
    .or(z.literal('')),
});

type UpdateTeamFormData = z.infer<typeof updateTeamSchema>;

/**
 * Format ISO date string to user-friendly display format.
 */
function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function TeamSettings({ team }: TeamSettingsProps): JSX.Element {
  const navigate = useNavigate();
  const { currentOrg } = useTenant();
  const updateTeam = useUpdateTeam();
  const deleteTeam = useDeleteTeam();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [confirmSlug, setConfirmSlug] = useState('');

  const {
    formState: { errors, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<UpdateTeamFormData>({
    resolver: zodResolver(updateTeamSchema),
    mode: 'onBlur',
    defaultValues: {
      name: team.name,
      description: team.description ?? '',
    },
  });

  // Sync form state with team data when it changes
  useEffect(() => {
    reset({
      name: team.name,
      description: team.description ?? '',
    });
  }, [team, reset]);

  const onSubmit = (data: UpdateTeamFormData): void => {
    updateTeam.mutate(
      {
        teamId: team.id,
        data: {
          name: data.name,
          description: data.description || undefined,
        },
        orgId: currentOrg?.id,
      },
      {
        onSuccess: () => {
          addToast({
            description: 'Team updated successfully',
            color: 'success',
          });
        },
        onError: (error: Error) => {
          addToast({
            description: error.message || 'Failed to update team',
            color: 'danger',
          });
        },
      },
    );
  };

  const handleDelete = (): void => {
    if (!currentOrg) return;

    deleteTeam.mutate(
      { teamId: team.id, orgId: currentOrg.id },
      {
        onSuccess: () => {
          addToast({
            description: 'Team deleted successfully',
            color: 'success',
          });
          void navigate('/org/teams', { replace: true });
        },
        onError: (error: Error) => {
          addToast({
            description: error.message || 'Failed to delete team',
            color: 'danger',
          });
        },
      },
    );
  };

  const handleModalClose = (): void => {
    setConfirmSlug('');
    onClose();
  };

  const slugMatches = confirmSlug === team.slug;

  let deleteSection: ReactNode;

  if (team.is_default) {
    deleteSection = (
      <div className="border-t border-default pt-4">
        <h3 className="font-semibold">Delete Team</h3>
        <p className="mt-2 text-sm">
          Default teams cannot be deleted. This team is automatically assigned to all new
          organization members.
        </p>
      </div>
    );
  } else {
    deleteSection = (
      <div className="border-t border-default pt-4">
        <h3 className="text-lg font-semibold mb-2">Delete Team</h3>
        <p className="mb-4 text-sm">
          Permanently delete <strong>{team.name}</strong>. This action cannot be undone.
        </p>
        <Button color="danger" onPress={onOpen}>
          Delete Team
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      <form noValidate onSubmit={e => void handleSubmit(onSubmit)(e)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="block text-sm font-medium mb-1">Team Slug</span>
            <div className="px-3 py-2 bg-surface-elevated rounded-lg text-foreground">
              {team.slug}
            </div>
            <p className="text-xs text-muted mt-1">
              Used in URLs and API calls. Cannot be changed.
            </p>
          </div>
          <div>
            <span className="block text-sm font-medium mb-1">Created</span>
            <div className="px-3 py-2 bg-surface-elevated rounded-lg text-foreground">
              {formatDate(team.created_at)}
            </div>
          </div>
        </div>
        <Input
          id="team-name"
          labelPlacement="outside-top"
          label="Team Name"
          placeholder="Enter team name"
          description="The display name for this team."
          isInvalid={!!errors.name}
          errorMessage={errors.name?.message}
          {...register('name')}
        />

        <Textarea
          id="team-description"
          label="Description"
          placeholder="Describe this team (optional)"
          description="A brief description of what this team does."
          isInvalid={!!errors.description}
          errorMessage={errors.description?.message}
          {...register('description')}
        />

        <Button type="submit" color="primary" isDisabled={updateTeam.isPending || !isDirty}>
          {updateTeam.isPending ? <LoadingSpinner /> : 'Save Changes'}
        </Button>
      </form>

      {deleteSection}

      <ConfirmationDialog
        content={
          <div className="flex flex-col gap-4">
            <p>
              This will permanently delete <strong>{team.name}</strong> and remove all team
              memberships. This action cannot be undone.
            </p>
            <p className="text-sm">
              To confirm, type the team slug:{' '}
              <Chip className="bg-surface-elevated rounded font-mono" size="sm">
                {team.slug}
              </Chip>
            </p>
            <Input
              labelPlacement="outside-top"
              placeholder={team.slug}
              value={confirmSlug}
              onChange={e => setConfirmSlug(e.target.value)}
              autoComplete="off"
            />
          </div>
        }
        isOpen={isOpen}
        onClose={handleModalClose}
        isDisabled={!slugMatches}
        isPending={deleteTeam.isPending}
        onConfirm={handleDelete}
        title="Delete Team"
      />
    </div>
  );
}
